import { Location, PopStateEvent } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import {
    ActivatedRoute,
    NavigationEnd,
    NavigationStart,
    Router
} from "@angular/router";
import { Store } from "@ngrx/store";
import { BroadcastEventListener, SignalR, SignalRConnection } from "ng2-signalr";
import { DeviceDetectorService } from "ngx-device-detector";
import { untilDestroyed } from "ngx-take-until-destroy";
import { Subscription } from "rxjs";
import { ToroLocationState } from "./accounts/store/toro-location/toro-location.entity";
import { getCurrentLocationDetail } from "./accounts/store/toro-location/toro-location.selectors";
import { Toro } from "./api/common/enumerations";
import { ApplicationStateManagerService } from "./api/managers/application-state-manager.service";
import { LoginAndUserManagerService } from "./api/managers/login-and-user-manager.service";
import { NotificationManagerService } from "./api/managers/notification-manager.service";
import { ToroLocationDetailed } from "./api/models/toro-location-detailed.model";
import { User } from "./api/models/user.model";
import { AuthState } from "./auth/store/auth.entity";
import { loggedInUser } from "./auth/store/auth.selectors";
import { Constants } from "./core/constants";
import { ConsoleToggleService } from "./core/services/consoleToggleService";
import { ProcessingOverlayParam } from "./models/processing-overlay-param.model";
import { BroadcastService } from "./services/broadcast.service";
import { OverlayManagerService } from "./services/overlay-manager.service";
import { AppConnectivityComponent } from "./shared/app-connectivity/app-connectivity.component";
import { ToroNotification } from "./api/models/toro-notification.model";
import { SceneState } from "./lighting/store/scenes/scene.entity";
import { addSceneSuccess } from "./lighting/store/scenes/scene.actions";
import { DeviceGroup } from "./api/models/device-group.model";
import { ToroLocation } from "./api/models/toro-location.model";
import { createLocationSuccess } from "./accounts/store/toro-location/toro-location.actions";
import { SceneScheduleSummaryState } from "./lighting/store/scene-schedule-summary/scene-schedule-summary.entity";
import { addSceneScheduleSummarySuccess } from "./lighting/store/scene-schedule-summary/scene-schedule-summary.actions";
import { ScheduleSummary } from "./api/models/schedule-summary.model";
import { ScenesStatusState } from "./lighting/store/scenes-status/scenes-status.entity";
import { loadScenesStatus } from "./lighting/store/scenes-status/scenes-status.actions";
import { ThemeState } from "./lighting/store/themes/theme.entity";
import { addThemeSuccess } from "./lighting/store/themes/theme.actions";
import { ThemesStatusState } from "./lighting/store/themes-status/themes-status.entity";
import { loadThemesStatus } from "./lighting/store/themes-status/themes-status.actions";
import { Idle, DEFAULT_INTERRUPTSOURCES } from "@ng-idle/core";
import { Keepalive } from "@ng-idle/keepalive";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.less"],
})
export class AppComponent implements OnInit, OnDestroy {
    @HostListener("window:resize") onWindowResize() {
        this.applicationStateManager.resizeWindow();
    }
    @ViewChild("connectionStatus") cs: AppConnectivityComponent;

    Constants = Constants;
    connection: SignalRConnection;
    showHeader = false;
    showFooter = false;
    isProcessing = false;
    processingMessage = "";
    isExternalTermsOrPrivacy = false;
    user: User;
    documentType = Toro.DocumentType.None;
    displayDocument = false;
    displayWhatsNew = false;
    documentName = "";
    documentData: any;
    documentHasMaxWidth = true;
    blurBackground = false;
    skipVersionCheck = false;
    private lastPoppedUrl: string;
    private yScrollStack: number[] = [];
    isMobileDevice: boolean;
    currentLocation: ToroLocationDetailed;
    locationSubscription: Subscription;
    loggedInUserSubscription: Subscription;
    signalRStatusCheckSubscription: Subscription;
    idleState = 'Not started.';
    timedOut = false;
    lastPing?: Date = null;
    userIdleDialogIsDisplayed: boolean;
    subscription!: Subscription;

    // =========================================================================================================================================================
    // C'tor, Init and Destroy
    // =========================================================================================================================================================

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private overlayManager: OverlayManagerService,
        private location: Location,
        private applicationStateManager: ApplicationStateManagerService,
        private broadcastService: BroadcastService,
        private deviceDetector: DeviceDetectorService,
        private store: Store<AuthState>,
        private sceneStore: Store<SceneState>,
        private sceneScheduleStore: Store<SceneScheduleSummaryState>,
        private sceneStatusStore: Store<ScenesStatusState>,
        private themeStore: Store<ThemeState>,
        private themeStatusStore: Store<ThemesStatusState>,
        private locationStore: Store<ToroLocationState>,
        private authManager: LoginAndUserManagerService,
        private changeDetectore: ChangeDetectorRef,
        private _signalR: SignalR,
        private notificationManager: NotificationManagerService,
        private consoleToggleService: ConsoleToggleService,
        private idle: Idle,
        private keepalive: Keepalive,
    ) {
        this.locationSubscription = this.locationStore.select(getCurrentLocationDetail).subscribe((loc) => {
            if (loc && loc !== null) {
                this.currentLocation = loc;
            }
        });
        this.consoleToggleService.disableConsoleInProduction();
    }

    ngOnInit(): void {
        this.loggedInUserSubscription = this.store.select(loggedInUser).subscribe((u) => {
            this.user = u;
            //console.log('user  ', this.user);
            if (this.user && this.user !== null) {
                this.initIdleTimeout(this.idle, this.keepalive);
                this.connectToNotificationHub();
            }
            else {
                this.stopIdleTimeout(this.idle);
                if (this.connection) {
                    this.connection.stop();
                }
            }
        });

        this.signalRStatusCheckSubscription = this.notificationManager.signalRConnectionStatusCheckNotification.subscribe(x => {
            if (this.user && this.user !== null) {
                this.connectToNotificationHub();
            }
            else {
                this.stopIdleTimeout(this.idle);
                if (this.connection) {
                    this.connection.stop();
                }
            }
        });

        let routes = ["register/confirm", "login/reset"];
        let currentPath = window.location.pathname.toLowerCase();
        this.route.queryParams.subscribe((qs) => {
            this.isMobileDevice = qs.ismobiledevice == "true";
        });
        routes.forEach(r => {
            if (currentPath.includes(r)) {
                this.skipVersionCheck = true;
            }
        });

        if (!this.skipVersionCheck) {
            this.authManager.checkLocalStorageVersion();
        }

        this.setBrowserType();

        this.location.subscribe((event: PopStateEvent) => {
            this.lastPoppedUrl = event.url;
        });

        this.router.events.subscribe((event) => {
            if (
                event instanceof NavigationStart &&
                event.url !== this.lastPoppedUrl
            ) {
                this.yScrollStack.push(window.scrollY);
            }

            if (event instanceof NavigationEnd) {
                this.showHeader = event.url !== "/welcome";
                this.showFooter = this.showHeader;
                this.isExternalTermsOrPrivacy =
                    event.url === "/terms" || event.url === "/privacy";

                if (event.url === this.lastPoppedUrl) {
                    this.lastPoppedUrl = undefined;
                    window.scrollTo(0, this.yScrollStack.pop());
                } else {
                    window.scrollTo(0, 0);
                }
            }
        });

        this.overlayManager.showProcessingOverlay
            .pipe(untilDestroyed(this))
            .subscribe((param: string | ProcessingOverlayParam) => {
                const message: string =
                    typeof param === "string" ? param : param.message;

                if (!message || message.trim() === "") return;

                this.processingMessage = message;
                this.isProcessing = true;
                this.changeDetectore.detectChanges();
            });

        this.overlayManager.hideProcessingOverlay
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.isProcessing = false;
                this.processingMessage = "";
                this.blurBackground = false;
                this.changeDetectore.detectChanges();
            });


        this.broadcastService.showPopupDialog
            .pipe(untilDestroyed(this))
            .subscribe(
                (docInfo: { documentType: Toro.DocumentType; data: any }) => {
                    this.showPopupDialog(docInfo);
                }
            );
    }

    stopIdleTimeout(idle: Idle) {
        idle.stop();
    }

    ngOnDestroy() {
        /** Implemented to support untilDestroyed() */

        if (this.locationSubscription) {
            this.locationSubscription.unsubscribe();
        }

        if (this.loggedInUserSubscription) {
            this.loggedInUserSubscription.unsubscribe();
        }

        if (this.signalRStatusCheckSubscription) {
            this.signalRStatusCheckSubscription.unsubscribe();
        }

        if (this.connection) {
            this.stopIdleTimeout(this.idle);
            this.connection.stop();
        }
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    onHideDialog() {
        this.overlayManager.toggleBlurBackground.next(false);
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private connectToNotificationHub() {
        // let con = this._signalR.createConnection();
        this.connection = this._signalR
            .createConnection({
                qs: { userId: this.user.Email, token: this.user.Token },
            });

        // 5. start the connection
        this.connection.start().then((c) => {

            // 1.create a listener object
            let onMessageSent$ = new BroadcastEventListener<any>(
                "healthCheck"
            );

            // 2. register the listener
            c.listen(onMessageSent$);

            // 3. subscribe for incoming messages
            onMessageSent$.subscribe((x) => {
                //console.log('  subscribe for incoming messages  ',x);

                this.cs.updateStatus(true);
                this.changeDetectore.detectChanges();
            });

            // 4. listen for connection status
            c.status.subscribe((x) => {
                //console.log('   listen for connection status  ',x);
                if (x.value == 2) {
                    // this.cs.updateStatus(false);

                    if (this.currentLocation && this.currentLocation.AccountId) {
                        this.notificationManager.restartPolling(this.currentLocation.AccountId, Constants.longPollInterval);
                    }

                    this.changeDetectore.detectChanges();
                } else {
                    this.cs.updateStatus(true);
                    this.notificationManager.stopPolling();
                    this.changeDetectore.detectChanges();
                }
            });

            // 6. listen for connection errors
            c.errors.subscribe((err) => {
                //console.log("SignalR Errors-", err);
                c.start().then(tc => {
                    console.log("Notification Hub Reconnected after error", tc);
                });
            });
            c.listenFor("getNotification").subscribe(
                (notification) => {

                    if ((notification['Notification'] && notification['Notification'] !== null) && (this.currentLocation && this.currentLocation !== null)) {
                        // console.log("Notification Receieved from SignalR 2",notification);
                        let signalRNotification = notification['Notification'];
                        this.notificationManager.notificationReceivedOverSignalR.next(new ToroNotification(signalRNotification));
                        // this.notificationManager.restartPolling(this.currentLocation.AccountId, Constants.longPollInterval);
                    }
                }
            );
            c.listenFor("newSceneCreatedNotification").subscribe((notification: any) => {
                if (notification) {
                    console.log("New Scene created notification");
                    this.sceneStore.dispatch(addSceneSuccess({ accountId: this.currentLocation.AccountId, locationId: this.currentLocation.Id, scene: new DeviceGroup(notification.Scene) }));
                    this.sceneScheduleStore.dispatch(addSceneScheduleSummarySuccess({ sceneScheduleSummary: new ScheduleSummary(notification.SceneScheduleSummary) }));
                    this.sceneStatusStore.dispatch(loadScenesStatus({ locationId: this.currentLocation.Id }));
                }
            });

            c.listenFor("newThemeCreatedNotification").subscribe((notification: any) => {
                if (notification) {
                    console.log("New Scene created notification");
                    this.themeStore.dispatch(addThemeSuccess({ theme: notification.Theme }));
                    this.themeStatusStore.dispatch(loadThemesStatus({ locationId: this.currentLocation.Id }));
                }
            });

            c.listenFor("newLocationCreatedNotification").subscribe((notification: any) => {
                if (notification) {
                    console.log("New Location created notification");
                    this.locationStore.dispatch(createLocationSuccess({ location: new ToroLocation(notification) }));
                }
            });

            c.listenFor("IsGatewayOnline").subscribe((x) => {
                if (
                    (x && x !== null && this.user && this.user !== null &&
                        this.user.Email === x["UserId"]) &&
                    (((x['Gateway'] && x['Gateway'] !== null) &&
                        (this.currentLocation && this.currentLocation !== null)) &&
                        x['Gateway'].LocationId === this.currentLocation.Id)
                ) {

                    this.notificationManager.isGatewayOnline.next(x);
                }
            });

        })
        .catch((err) => {
            console.log('signalR not working so switch to old long polling ',err);
            if (this.currentLocation && this.currentLocation.AccountId && this.user && this.user !== null) {
                this.notificationManager.restartPolling(this.currentLocation.AccountId, Constants.longPollInterval);
            }
        });
    }

    private setBrowserType() {
        const el = document.getElementsByTagName("html")[0];

        let attr = document.createAttribute("browser-name");
        attr.value = this.deviceDetector.getDeviceInfo().browser;
        el.attributes.setNamedItem(attr);

        attr = document.createAttribute("browser-version");
        attr.value = this.deviceDetector.getDeviceInfo().browser_version;
        el.attributes.setNamedItem(attr);
    }

    private showPopupDialog(docInfo: {
        documentType: Toro.DocumentType;
        data: any;
    }) {
        this.documentData = null;
        this.documentHasMaxWidth = true;

        switch (docInfo.documentType) {
            case Toro.DocumentType.About:
                this.documentName = "About";
                break;

            case Toro.DocumentType.Support:
                this.documentName = "Support";
                break;

            case Toro.DocumentType.TermsOfService:
                this.documentName = "Terms of Service";
                break;

            case Toro.DocumentType.PrivacyPolicy:
                this.documentName = "Privacy Policy";
                break;

            case Toro.DocumentType.PinInfo:
                this.documentName = "PIN Information";
                break;

            case Toro.DocumentType.Configuration:
                this.documentName = "Controller Configuration";
                this.documentData = docInfo.data;
                this.documentHasMaxWidth = false;
                break;

            default:
                this.documentName = "---";
                break;
        }

        this.overlayManager.toggleBlurBackground.next(true);
        this.documentType = docInfo.documentType;
        this.displayDocument = !this.displayDocument;
    }

    initIdleTimeout(idle : Idle, keepalive : Keepalive){
        if (this.user && this.user !== null) {
            // set idle parameters
            idle.setIdle(Constants.SystemIdleTimeoutInSec);       // how long can they be inactive before considered idle, in seconds
            idle.setTimeout(Constants.TimeoutInSec);        // how long can they be idle before considered timed out, in seconds
            idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);   // provide sources that will interrupt/provide events indicating the user is active.
                                                            // in this case, things like clicks, scrolls, touches to the document

            idle.onIdleEnd.subscribe(() => {
                // this.idleState = 'No longer idle.'
            });

            idle.onTimeout.subscribe(() => {
                this.idleState = 'Timed out!';
                this.timedOut = true;
                this.logoutUser();
            });

            idle.onIdleStart.subscribe(() =>{
                this.idleState = 'You\'ve gone idle!'
                this.overlayManager.toggleBlurBackground.next(true);
                this.userIdleDialogIsDisplayed = true;
            });

            idle.onTimeoutWarning.subscribe((countdown) => {
                this.idleState = 'Your session will get timed out due to inactivity.'
            });

            // sets the ping interval to 15 seconds
            keepalive.interval(15);
            keepalive.onPing.subscribe(() => this.lastPing = new Date());
            this.reset();
        }
    }

    reset() {
        this.idle.watch();
        //this.idleState = 'Started.';
        this.userIdleDialogIsDisplayed = false;
        this.timedOut = false;
        this.lastPing = null;
    }

    onCloseDialog() {
        this.userIdleDialogIsDisplayed = false;
    }

    logoutUser() {
        this.authManager.signOut();
        this.onCloseDialog();
    }

    extendUser() {
        this.onCloseDialog();
        //this.reset();
        window.location.reload();
    }

}
